<script>
import { mapState } from "vuex";
import fileDownload from "js-file-download";
import dayjs from "dayjs";
import Multiselect from "vue-multiselect";

import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import PageHeader from "@/components/page-header";
import CreativePreviewModal from "@/components/campaigns/creative-preview-modal";

export default {
  page: {
    title: "Campaigns",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    CreativePreviewModal,
    Multiselect
  },
  methods: {
    toggleModal(id) {
      this.$root.$emit('bv::toggle::modal', `campaign-creative-preview-modal-${id}`, '#btnToggle')
    },
    getFilterParams() {
      return {
        businessType: this.filters.businessType ? this.filters.businessType : null,
        masthead: this.filters.masthead ? this.filters.masthead : null,
        packageType: this.filters.packageType ? this.filters.packageType : null,
        status: this.filters.status ? this.filters.status : null,
      }
    },
    async fetchData(ctx) {
      return await this.apiGet(`/campaigns`, { exclude: 'businessAddress,callToActionType,campaignImages,contactImage,logoImage,', include: 'createdBy,lastCampaignStat', sort: ctx.sortBy, order: ctx.sortDesc ? 'desc' : 'asc', limit: ctx.perPage, page: ctx.currentPage, searchString: ctx.filter, ...this.getFilterParams()}).then((data) => {
        this.totalRows = data ? data.meta.cursor.count : 0;
        return data.data;
      }).catch((error) => {
        this.$store.dispatch('layout/addPageError', error);
      })
    },
    handleCsvExport() {
      this.apiGetFile(`/campaigns/report`, {}, 'application/csv').then((data) => {
        fileDownload(data, `advantage-campaigns-${dayjs().format('DD-MM-YYYY')}.csv`)
      }).catch((error) => {
        this.$store.dispatch('layout/addPageError', error);
      })
    },
    handleBusinessTypeSelect({ id }) {
      this.filters.businessType = id

      this.$refs.table.refresh();
    },
    handleStatusSelect(status) {
      this.filters.status.push(status);

      this.$refs.table.refresh();
    },
    handleStatusRemove(status) {
      let index = this.filters.status.indexOf(status);

      if (index !== -1) {
        this.filters.status.splice(index, 1);
      }
      this.$refs.table.refresh();
    },
    handleMastheadSelect({ id }) {
      this.filters.masthead = id

      this.$refs.table.refresh();
    },
    handlePackageTypeSelect(packageType) {
      this.filters.packageType.push(packageType);

      this.$refs.table.refresh();
    },
    handlePackageTypeRemove(packageType) {
      let index = this.filters.packageType.indexOf(packageType);

      if (index !== -1) {
        this.filters.packageType.splice(index, 1);
      }
      this.$refs.table.refresh();
    },
    clearFilters() {
      this.filters = {
        businessType: '',
        masthead: '',
        packageType: '',
        status: [],
      };

      this.$refs.table.refresh();
    },
  },
  data() {
    return {
      title: "Campaigns",
      items: [
        {
          text: "Campaigns",
          href: "/campaigns",
        },
        {
          text: "Index",
          active: true,
        },
      ],
      showModal: false,
      totalRows: 1,
      currentPage: 1,
      perPage: 20,
      pageOptions: [20, 50, 100, 200],
      businessTypeOptions: [],
      mastheadOptions: [],
      packageTypeOptions: [
        'Digital',
        'Print & digital',
        'Video',
      ],
      statusOptions: [
        'Cancelled',
        'Completed',
        'Draft',
        'In Progress',
        'Incomplete',
        'Upcoming',
      ],
      filter: null,
      filters: {
        businessType: '',
        masthead: '',
        packageType: [],
        status: [],
      },
      filterOn: [],
      sortBy: "updatedAt",
      sortDesc: true,
      fields: [
        { key: "name", label: "Name", sortable: true },
        { key: "startDate", label: "Start Date", sortable: true },
        { key: "package.name", label: "Package", sortable: true },
        { key: "lastCampaignStat.totalClickThroughRate", label: "CTR", sortable: false },
        { key: "status", label: "Campaign Status", sortable: true },
        { key: "createdBy.fullName", label: "Booked By", sortable: true },
        { key: "action", label: "", sortable: false },
        { key: "edit", label: "", sortable: false }
      ]
    };
  },
  computed: {
    ...mapState('auth', ['currentUser']),
    businessTypeValue() {
      return this.businessTypeOptions ? this.businessTypeOptions.find((businessType) => businessType.id === this.filters.businessType) : null;
    },
    mastheadValue() {
      return this.mastheadOptions ? this.mastheadOptions.find((masthead) => masthead.id === this.filters.masthead) : null;
    },
    packageTypeValue() {
      return this.filters.packageType;
    },
    statusValue() {
      return this.filters.status;
    },
  },
  async mounted() {
    this.apiGet(`/business-types`, {}, true).then((data) => {
      data.data.forEach(businessTypeData => {
        this.businessTypeOptions.push(businessTypeData);
        businessTypeData.children.forEach(childrenData => {
          childrenData.name = `\xa0- ${childrenData.name}`;
          this.businessTypeOptions.push(childrenData);
        });
      });
    }).catch((error) => {
      this.$store.dispatch('layout/addPageError', error);
    })

    this.apiGet(`/mastheads`, {}, true).then((data) => {
      this.mastheadOptions = data.data
    }).catch((error) => {
      this.$store.dispatch('layout/addPageError', error);
    })
  }
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row mb-3">
      <div class="col-12 text-right">
        <router-link to="/campaigns/create/name">
          <b-button variant="primary">
            <i class="bx bxs-plus-circle font-size-16 align-middle mr-2"></i> Add New
          </b-button>
        </router-link>
        <b-button variant="outline-primary" class="ml-2" @click="handleCsvExport">
          Export as CSV
        </b-button>
      </div>
    </div>

    <div class="row mb-1 justify-content-end">
      <div class="col-12 col-md-2">
        <multiselect 
          :options="businessTypeOptions" 
          :value="businessTypeValue" 
          @select="handleBusinessTypeSelect" 
          class="mb-2"
          deselect-label=""
          label="name" 
          placeholder="Filter by Business Type"
          select-label=""
          track-by="id" 
        ></multiselect>
      </div>
      <div class="col-12 col-md-2">
        <multiselect
          :multiple="true"
          :options="statusOptions"
          :value="statusValue"
          @remove="handleStatusRemove"
          @select="handleStatusSelect"
          class="mb-2"
          deselect-label=""
          placeholder="Filter by Campaign Status"
          select-label=""
        ></multiselect>
      </div>
      <div class="col-12 col-md-2">
        <multiselect
          :options="mastheadOptions"
          :value="mastheadValue"
          @select="handleMastheadSelect"
          class="mb-2"
          deselect-label=""
          label="name"
          placeholder="Filter by Masthead"
          select-label=""
          track-by="id"
        ></multiselect>
      </div>
      <div class="col-12 col-md-2">
        <multiselect 
          :multiple="true"
          :options="packageTypeOptions"
          :value="packageTypeValue" 
          @remove="handlePackageTypeRemove"
          @select="handlePackageTypeSelect" 
          deselect-label=""
          placeholder="Filter by Package Type"
          select-label=""
        ></multiselect>
      </div>
      <div class="col-12 col-md-2 mb-2">
        <b-button class="w-100" @click="clearFilters" variant="primary">Clear Filters</b-button>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row">
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Show&nbsp;
                    <b-form-select v-model="perPage" size="sm" :options="pageOptions"></b-form-select>&nbsp;entries
                  </label>
                </div>
              </div>
              <!-- Search -->
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_filter" class="dataTables_filter text-md-right">
                  <label class="d-inline-flex align-items-center">
                    Search:
                    <b-form-input
                      debounce="500"
                      v-model="filter"
                      type="search"
                      placeholder="Search..."
                      class="form-control form-control-sm ml-2"
                    ></b-form-input>
                  </label>
                </div>
              </div>
              <!-- End search -->
            </div>
            <!-- Table -->
            <div class="table-responsive mb-0">
              <b-table
                ref="table"
                :items="fetchData"
                :fields="fields"
                responsive="sm"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                :filter-included-fields="filterOn"
                no-local-sorting
              >
                <template v-slot:cell(startDate)="row">
                  {{ row.item.startDate | formatDate }}
                </template>
                <template v-slot:cell(lastCampaignStat.totalClickThroughRate)="row">
                  <span class="badge badge-pill badge-soft-primary font-size-12">{{ row.item.lastCampaignStat ? `${row.item.lastCampaignStat.totalClickThroughRate}%` : '' }}</span>
                </template>
                <template v-slot:cell(status)="row">
                  <span class="badge badge-pill font-size-12" :class="getBadgeClassByStatus(row.item.status)">
                    {{ row.item.status }}
                  </span>
                </template>
                <template v-slot:cell(createdBy.email)="row">
                  {{ row.item.createdBy.firstName }} {{ row.item.createdBy.lastName }}
                </template>
                <template v-slot:cell(action)="row">
                  <creative-preview-modal :id="`campaign-creative-preview-modal-${row.item.id}`" :campaignId="row.item.id" />
                  <a href="#" @click="toggleModal(row.item.id)" ref="btnToggle" v-if="row.item.status === 'Draft'">
                    View Creative <i class="bx bx-chevron-right" />
                  </a>
                  <router-link v-else-if="row.item.status !== 'incomplete'" :to="`/campaigns/${row.item.id}`" class="d-flex align-items-center">
                    View Dashboard <i class="bx bx-chevron-right" />
                  </router-link>
                </template>
                <template v-slot:cell(edit)="row">
                  <router-link v-if="row.item.status === 'Incomplete' || row.item.status === 'Draft' || ((row.item.status === 'Upcoming' || row.item.status === 'In Progress') && (currentUser.role === 'ROLE_SUPER_ADMIN' || currentUser.role === 'ROLE_ADMIN'))" :to="`/campaigns/${row.item.id}/edit`" class="d-flex align-items-center">
                    Edit Campaign <i class="bx bx-chevron-right" />
                  </router-link>
                </template>
              </b-table>
            </div>
            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <!-- pagination -->
                    <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>